"use client";

import { useSearchParams } from "next/navigation";
import { openingLineIndex } from "@/lib/explore";
import { Suspense, useEffect, useMemo, useState } from "react";
import { Chess, type Square } from "chess.js";
import { BoardView } from "@/components/board/BoardView";
import { continuations, loadOpenings, type OpeningRow } from "@/lib/chess/openingIndex";

type Variation = { eco: string; name: string; variation: string; san: string[]; uci: string[] };

type ExplorerMove = { uci: string; san: string; white: number; draws: number; black: number; averageRating?: number };
type ExplorerData = { white: number; draws: number; black: number; moves: ExplorerMove[] };

/** Steps through a chosen line and shows what comes next from live stats or the catalogue. */
export function OpeningExplorer(props: { variations: Variation[]; familyName: string }) {
  // The explorer prerenders on the main line so its moves and lines are in the HTML;
  // only the ?line= reader waits for the client (useSearchParams opts its boundary out
  // of static rendering) and switches to the requested line after hydration.
  const [initial, setInitial] = useState(0);
  return (
    <>
      <Suspense fallback={null}>
        <LineParam count={props.variations.length} onLine={setInitial} />
      </Suspense>
      <OpeningExplorerContent key={`${props.familyName}-${initial}`} {...props} initial={initial} />
    </>
  );
}

function LineParam({ count, onLine }: { count: number; onLine: (line: number) => void }) {
  const line = useSearchParams().get("line");
  useEffect(() => onLine(openingLineIndex(line, count)), [line, count, onLine]);
  return null;
}

function OpeningExplorerContent({ variations, initial }: { variations: Variation[]; familyName: string; initial: number }) {
  const [vi, setVi] = useState(initial);
  const [ply, setPly] = useState(variations[initial].uci.length);
  const [filter, setFilter] = useState("");
  const [limit, setLimit] = useState(40);
  const [result, setResult] = useState<{ fen: string; state: "ok" | "off" | "error"; data: ExplorerData | null } | null>(null);
  const [rows, setRows] = useState<OpeningRow[]>([]);
  const v = variations[vi];

  useEffect(() => {
    void loadOpenings().then(setRows);
  }, []);

  const { fen, lastMove } = useMemo(() => {
    const c = new Chess();
    let last: { from: Square; to: Square } | null = null;
    for (const u of v.uci.slice(0, ply)) {
      const m = c.move({ from: u.slice(0, 2), to: u.slice(2, 4), promotion: u[4] });
      last = { from: m.from, to: m.to };
    }
    return { fen: c.fen(), lastMove: last };
  }, [v, ply]);

  useEffect(() => {
    let alive = true;
    fetch(`/api/explorer?fen=${encodeURIComponent(fen)}`)
      .then(async (r) => {
        const body = r.ok ? ((await r.json()) as ExplorerData & { configured?: boolean }) : null;
        const state = !body ? "error" : body.configured === false ? "off" : "ok";
        if (alive) setResult({ fen, state, data: state === "ok" ? body : null });
      })
      .catch(() => alive && setResult({ fen, state: "error", data: null }));
    return () => {
      alive = false;
    };
  }, [fen]);

  // Results belong to the position they were fetched for; anything else is still loading.
  const statsState = result?.fen === fen ? result.state : "loading";
  const stats = result?.fen === fen ? result.data : null;

  const named = useMemo(() => (rows.length ? continuations(rows, v.uci.slice(0, ply)).slice(0, 8) : []), [rows, v, ply]);
  const list = variations
    .map((x, i) => ({ ...x, i }))
    .filter((x) => !filter || `${x.name} ${x.eco} ${x.san.join(" ")}`.toLowerCase().includes(filter.toLowerCase()));

  const sanFor = (uci: string) => {
    try {
      return new Chess(fen).move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] }).san;
    } catch {
      return uci;
    }
  };

  const moveText = v.san.map((s, i) => (
    <button
      key={i}
      onClick={() => setPly(i + 1)}
      className={`rounded px-1 font-mono text-sm ${i + 1 === ply ? "bg-accent-soft font-semibold" : "hover:bg-surface-2"}`}
    >
      {i % 2 === 0 ? `${i / 2 + 1}.` : ""}
      {s}
    </button>
  ));

  return (
    <div id="opening-explorer" className="grid scroll-mt-28 gap-6 lg:grid-cols-[minmax(0,440px)_1fr]">
      <div>
        <BoardView view="3d" fen={fen} lastMove={lastMove} label={`${v.name} board`} />
        <div className="mt-2 flex flex-wrap items-center gap-1">
          <button onClick={() => setPly(0)} className="rounded border border-border px-2 py-1 text-sm" aria-label="Start">«</button>
          <button onClick={() => setPly(Math.max(0, ply - 1))} className="rounded border border-border px-2 py-1 text-sm" aria-label="Back">‹</button>
          <button onClick={() => setPly(Math.min(v.uci.length, ply + 1))} className="rounded border border-border px-2 py-1 text-sm" aria-label="Forward">›</button>
          <button onClick={() => setPly(v.uci.length)} className="rounded border border-border px-2 py-1 text-sm" aria-label="End">»</button>

        </div>
        <p className="mt-3 text-sm">
          <span className="text-muted">{v.eco}</span> <strong>{v.name}</strong>
        </p>
        <div className="mt-1 flex flex-wrap gap-0.5">{moveText}</div>

      </div>

      <div className="space-y-6">
        <section aria-labelledby="next-moves">
          <h2 id="next-moves" className="text-xl">
            What players choose here
          </h2>
          {statsState === "ok" && stats && stats.moves.length > 0 && (
            <table className="mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="font-normal">Move</th>
                  <th className="font-normal">Games</th>
                  <th className="w-1/2 font-normal">White / draw / Black</th>
                </tr>
              </thead>
              <tbody>
                {stats.moves.map((m) => {
                  const n = m.white + m.draws + m.black;
                  const pct = (x: number) => (n ? Math.round((x / n) * 100) : 0);
                  return (
                    <tr key={m.uci} className="border-t border-border">
                      <td className="py-1 font-mono font-semibold">{m.san}</td>
                      <td className="text-muted">{n.toLocaleString("en-US")}</td>
                      <td>
                        <div className="flex h-4 overflow-hidden rounded text-[10px] leading-4" aria-label={`White wins ${pct(m.white)} percent, draws ${pct(m.draws)} percent, Black wins ${pct(m.black)} percent`}>
                          <span className="bg-[#f3ead8] pl-1 text-[#2a221d]" style={{ width: `${pct(m.white)}%` }}>{pct(m.white) >= 12 ? `${pct(m.white)}%` : ""}</span>
                          <span className="bg-stone-400 pl-1 text-white" style={{ width: `${pct(m.draws)}%` }}>{pct(m.draws) >= 12 ? `${pct(m.draws)}%` : ""}</span>
                          <span className="bg-[#2a221d] pl-1 text-[#f3ead8]" style={{ width: `${pct(m.black)}%` }}>{pct(m.black) >= 12 ? `${pct(m.black)}%` : ""}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {statsState === "loading" && <p className="mt-2 text-sm text-muted">Loading game statistics…</p>}
          {(statsState === "off" || statsState === "error" || (statsState === "ok" && !stats?.moves.length)) && (
            <p className="mt-2 text-sm text-muted">
              {statsState === "off"
                ? "Live statistics from Lichess games appear here once the site has a Lichess API token. Meanwhile, these are the named continuations:"
                : statsState === "error"
                  ? "Live statistics are unavailable right now. These are the named continuations:"
                  : "No games reached this position in the database. Named continuations:"}
            </p>
          )}
          {statsState !== "ok" && statsState !== "loading" && (
            <ul className="mt-2 space-y-1 text-sm">
              {named.map((n) => (
                <li key={n.uci}>
                  <span className="font-mono font-semibold">{sanFor(n.uci)}</span> <span className="text-muted">{n.names[0]}{n.lines > 1 ? ` and ${n.lines - 1} more lines` : ""}</span>
                </li>
              ))}
              {named.length === 0 && <li className="text-muted">The catalogue has no named line beyond this point.</li>}
            </ul>
          )}
        </section>

        <section aria-labelledby="lines">
          <h2 id="lines" className="text-xl">
            {variations.length === 1 ? "The only named line" : `All ${variations.length} lines`}
          </h2>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter lines"
            aria-label="Filter lines"
            className="mt-2 w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm"
          />
          <ul className="mt-2 divide-y divide-border rounded-xl border border-border bg-surface">
            {list.slice(0, limit).map((x) => (
              <li key={x.i}>
                <button
                  onClick={() => {
                    setVi(x.i);
                    setPly(x.uci.length);
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm ${x.i === vi ? "bg-accent-soft" : "hover:bg-surface-2"}`}
                >
                  <span className="text-muted">{x.eco}</span> {x.variation || x.name}
                  <span className="block truncate font-mono text-xs text-muted">{x.san.join(" ")}</span>
                </button>
              </li>
            ))}
          </ul>
          {list.length > limit && (
            <button onClick={() => setLimit(limit + 60)} className="mt-2 text-sm text-accent hover:underline">
              Show more ({list.length - limit} left)
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
